import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
// @ts-ignore
import { KrpanoPlayer } from 'pano-sdk';
import produce from 'immer';
import {
  type CFScene,
  type CFSceneView,
  type Hotspot,
  type KrpPlayer,
  type SceneData,
  SceneType,
  type TourData,
} from '@types';
import Title from '@components/Title';
import Button from '@components/Button';
import Modal from '@components/Modal';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import useTourEditing from '@hooks/useTourEditing';
import { sendWarning } from '@components/Noti';
import useToggle from '@hooks/useToggle';
import { loadScene } from '@modules/player';
import { krpAddHotspot } from '@modules/hotspot';
import { viewDefault } from '@modules/view';
import { switchTypeItems } from '@modules/panel/constants';
import ImgArrow from './img-arrow.png';
import SwitchViewSetting from './SwitchViewSetting';
import ImgBlock from './img-block.png';

type ExitTypeId = Required<CFScene>['exitTargetView']['type'];
const exitItems: DropdownItem<ExitTypeId>[] = [
  { id: 0, name: '无动作' },
  { id: 1, name: '视角移动到热点' },
  { id: 2, name: '移动到指定视角' },
];
const exitMItems: DropdownItem<ExitTypeId>[] = [
  { id: 0, name: '无动作' },
  { id: 1, name: '视角移动到热点' },
];

type EnterInitTypeId = Required<CFScene>['enterInitView']['type'];
const enterInitItems: DropdownItem<EnterInitTypeId>[] = [
  { id: 0, name: '目标场景的初始视角' },
  { id: 1, name: '原始场景的离场视角' },
  { id: 2, name: '自定义视角' },
];
const enterInitMSItems: DropdownItem<EnterInitTypeId>[] = [
  { id: 0, name: '目标场景的初始视角' },
  { id: 2, name: '自定义视角' },
];
const enterInitMItems: DropdownItem<EnterInitTypeId>[] = [
  { id: 0, name: '目标场景的初始视角' },
];

type EnterTargetTypeId = Required<CFScene>['enterTargetView']['type'];
const enterTargetItems: DropdownItem<EnterTargetTypeId>[] = [
  { id: 0, name: '无动作' },
  { id: 1, name: '自定义视角' },
];
const enterTargetMItems: DropdownItem<EnterTargetTypeId>[] = [
  { id: 0, name: '无动作' },
];

const changeSceneView = (
  sceneData: SceneData,
  { hotspot, view }: { hotspot?: Hotspot; view?: CFSceneView } = {}
) => {
  if (hotspot) {
    return produce(sceneData, (draft) => {
      draft.view.hLookAt = hotspot.icon.ath ?? viewDefault.hLookAt;
      draft.view.vLookAt = hotspot.icon.atv ?? viewDefault.vLookAt;
    });
  }
  if (view) {
    const { ath, atv, fov } = view;
    if (![ath, atv, fov].includes(undefined)) {
      return produce(sceneData, (draft) => {
        draft.view.hLookAt = ath!;
        draft.view.vLookAt = atv!;
        draft.view.fov = fov!;
      });
    }
  }
  return sceneData;
};

const tween = 'smooth(100,100,100)';

const StyledModal = styled(Modal)`
  width: calc(100% - 60px);
  height: calc(100% - 60px);
`;

type SettingData = Pick<
  CFScene,
  'exitTargetView' | 'enterInitView' | 'enterTargetView' | 'animation'
>;

type Props = {
  exitScene: SceneData;
  enterScene: SceneData;
  dragMode: TourData['flag']['dragMode'];
  hotspot?: Hotspot;
  data: SettingData;
  onConfirm: (data: SettingData) => void;
  onRequestClose: () => void;
};

function SwitchSettingModal({
  exitScene,
  enterScene,
  dragMode = 1,
  hotspot,
  data,
  onConfirm,
  onRequestClose,
}: Props) {
  const [tab, setTab] = useState<'exit' | 'enter' | 'preview'>('exit');
  const [enterTab, setEnterTab] = useState<'start' | 'end'>('start');
  const { state, setState } = useTourEditing(data, {
    exitTargetView: { type: 0 },
    enterInitView: { type: 0 },
    enterTargetView: { type: 0 },
  });
  const playerRef = useRef<KrpPlayer | null>(null);
  const showCaputre =
    (tab === 'exit' && state.exitTargetView?.type === 2) ||
    (tab === 'enter' &&
      enterTab === 'start' &&
      state.enterInitView?.type === 2) ||
    (tab === 'enter' &&
      enterTab === 'end' &&
      state.enterTargetView?.type === 1);
  const handlerRef = useRef<(() => void) | null>(null);
  const previewTip = useToggle(true);

  const isExitMatrix = exitScene.type === SceneType.matrix;
  const isEnterMatrix = enterScene.type === SceneType.matrix;

  useEffect(() => {
    const newPlayer = new KrpanoPlayer() as KrpPlayer;
    newPlayer.createPlayer(
      'switch',
      {
        onHotspotClick: () => {
          handlerRef.current?.();
        },
      },
      () => {
        playerRef.current = newPlayer;
        newPlayer.controlMode({ mouse: dragMode ? 'drag' : 'moveto' });
        loadScene({
          player: newPlayer,
          data: exitScene,
          options: { onComplete: addHotspot },
        });
      }
    );
    return () => {
      playerRef.current?.removePlayer();
    };
  }, []);

  useEffect(() => {
    if (tab === 'preview') {
      handleEnterPreview();
    }
  }, [state.animation]);

  const handleConfirm = () => {
    const arr: string[] = [];
    if (
      state.exitTargetView?.type === 2 &&
      state.exitTargetView.ath === undefined
    ) {
      arr.push('请设置原始场景离场视角');
    }
    if (
      state.enterInitView?.type === 2 &&
      state.enterInitView.ath === undefined
    ) {
      arr.push('请设置目标场景起始视角');
    }
    if (
      state.enterTargetView?.type === 1 &&
      state.enterTargetView.ath === undefined
    ) {
      arr.push('请设置目标场景结束视角');
    }
    if (arr.length) {
      arr.forEach((msg) => {
        sendWarning(msg);
      });
    } else {
      onConfirm(state);
      onRequestClose();
    }
  };

  const addHotspot = () => {
    if (!playerRef.current || !hotspot) return;
    krpAddHotspot(hotspot, playerRef.current);
    playerRef.current.updateHotspot({ id: hotspot.id, dragable: false });
  };

  const lookTo = (
    view:
      | CFSceneView
      | CFScene['exitTargetView']
      | CFScene['enterInitView']
      | CFScene['enterTargetView'],
    onComplete?: () => void
  ) => {
    if (!view) return;
    const { ath, atv, fov } = view;
    if (![ath, atv, fov].includes(undefined)) {
      playerRef.current?.lookTo(
        {
          fov,
          hlookat: ath,
          vlookat: atv,
          tween,
        },
        onComplete
      );
    }
  };

  const lookToScene = (sceneData: SceneData) => {
    const { fov, hLookAt, vLookAt } = sceneData.view;
    lookTo({ ath: hLookAt, atv: vLookAt, fov });
  };

  const handleExitTypeChange = (typeId: ExitTypeId) => {
    if (typeId === 1 && hotspot) {
      playerRef.current?.lookToHotspot({ id: hotspot.id, fov: 60, tween });
    } else if (typeId === 2) {
      lookTo(state.exitTargetView);
    }
    setState((draft) => {
      draft.exitTargetView!.type = typeId;
    });
  };

  const handleEnterTypeChange = (typeId: EnterInitTypeId) => {
    if (typeId === 0) {
      lookToScene(enterScene);
    } else if (typeId === 2) {
      lookTo(state.enterInitView);
    }
    setState((draft) => {
      draft.enterInitView!.type = typeId;
    });
  };

  const handleCapture = () => {
    let key: keyof Pick<
      CFScene,
      'exitTargetView' | 'enterInitView' | 'enterTargetView'
    >;
    if (tab === 'enter') {
      key = enterTab === 'end' ? 'enterTargetView' : 'enterInitView';
    } else {
      key = 'exitTargetView';
    }
    if (!playerRef.current) return;
    const { hlookat, vlookat, fov } = playerRef.current.getView();
    setState((draft) => {
      draft[key]!.ath = hlookat;
      draft[key]!.atv = vlookat;
      draft[key]!.fov = fov;
    });
  };

  const initExitScene = () => {
    if (!playerRef.current) return;
    let sceneData = exitScene;
    if (state.exitTargetView?.type === 1) {
      sceneData = changeSceneView(exitScene, { hotspot });
    } else if (state.exitTargetView?.type === 2) {
      sceneData = changeSceneView(exitScene, { view: state.exitTargetView });
    }
    loadScene({
      player: playerRef.current,
      data: sceneData,
      options: { onComplete: addHotspot },
    });
    setTab('exit');
  };

  const handleExitTabClick = () => {
    if (tab === 'exit') return;
    initExitScene();
  };

  const handleEnterTabClick = () => {
    if (tab === 'enter') return;
    if (!playerRef.current) return;
    let sceneData = enterScene;
    if (state.enterInitView?.type === 2) {
      sceneData = changeSceneView(enterScene, { view: state.enterInitView });
    }
    loadScene({
      player: playerRef.current,
      data: sceneData,
      options: {
        keepView: state.enterInitView?.type === 1,
        blendType: state.animation,
      },
    });
    setEnterTab('start');
    setTab('enter');
  };

  const handleStartTabClick = () => {
    if (enterTab === 'start') return;
    setEnterTab('start');
    const type = state.enterInitView?.type;
    if (type === 0) {
      lookToScene(enterScene);
    } else if (type === 2) {
      lookTo(state.enterInitView);
    }
  };

  const handleEndTabClick = () => {
    if (enterTab === 'end') return;
    setEnterTab('end');
    if (state.enterTargetView?.type === 1) {
      lookTo(state.enterTargetView);
    }
  };

  const handleEnterPreview = () => {
    setTab('preview');
    const onComplete = () => {
      if (!playerRef.current) return;
      let sceneData = enterScene;
      if (state.enterInitView?.type === 2) {
        sceneData = changeSceneView(enterScene, { view: state.enterInitView });
      }
      loadScene({
        player: playerRef.current,
        data: sceneData,
        options: {
          keepView: state.enterInitView?.type === 1,
          blendType: state.animation,
          onComplete: () => {
            if (state.enterTargetView?.type === 1) {
              lookTo(state.enterTargetView);
            }
          },
        },
      });
    };

    handlerRef.current = () => {
      previewTip.off();
      const type = state.exitTargetView?.type;
      if (type === 1 && hotspot) {
        playerRef.current?.lookToHotspot(
          { id: hotspot.id, fov: 60, tween },
          onComplete
        );
      } else if (type === 2) {
        lookTo(state.exitTargetView, onComplete);
      } else {
        onComplete();
      }
    };
    loadScene({
      player: playerRef.current,
      data: changeSceneView(exitScene, { hotspot }),
      options: {
        onComplete: () => {
          if (hotspot) {
            addHotspot();
          } else {
            handlerRef.current?.();
          }
        },
      },
    });
  };

  const handleQuitPreview = () => {
    setTab('exit');
    handlerRef.current = null;
    initExitScene();
  };

  const renderTip = (tip: string) => {
    return (
      <div
        className="pointer-events-none absolute top-75 flex h-30 items-center rounded bg-orange px-10"
        style={{ zIndex: 2800 }}
      >
        {tip}
      </div>
    );
  };

  const renderHeader = () => {
    return (
      <div
        className="absolute top-0 right-0 left-0 flex h-60 items-center justify-center bg-black-black bg-opacity-50"
        style={{ zIndex: 2800 }}
      >
        {tab === 'preview' ? (
          <div>预览模式</div>
        ) : (
          <>
            <Button
              scheme={tab === 'enter' ? 'gray' : 'blue'}
              onClick={handleExitTabClick}
            >
              离场设置（原始场景）
            </Button>
            <img src={ImgArrow} alt="" className="mx-15 h-24 w-24" />
            <Button
              scheme={tab === 'exit' ? 'gray' : 'blue'}
              onClick={handleEnterTabClick}
            >
              进场设置（目标场景）
            </Button>
          </>
        )}
        <div className="absolute right-10 flex items-center">
          <div className="mr-10 w-120">
            <Dropdown
              scheme="light"
              items={switchTypeItems}
              selected={state.animation ?? 0}
              onChange={(typeId) => {
                setState((draft) => {
                  draft.animation = typeId;
                });
              }}
            />
          </div>
          {tab === 'preview' ? (
            <Button onClick={handleQuitPreview}>退出预览</Button>
          ) : (
            <Button onClick={handleEnterPreview}>预览效果</Button>
          )}
        </div>
      </div>
    );
  };

  const renderExitSetting = () => {
    const { type, ath, atv, fov } = state.exitTargetView!;
    let items = isExitMatrix ? exitMItems : exitItems;
    if (!hotspot) {
      items = items.filter((x) => x.id !== 1);
    }
    return (
      <>
        <div className="absolute top-70 left-10 w-200" style={{ zIndex: 2800 }}>
          <div className="rounded bg-black-#28 py-15 px-10">
            <div>选择点击后的离场动作</div>
            <Dropdown
              className="mt-15"
              items={items}
              selected={type}
              onChange={handleExitTypeChange}
            />
            <div className="mt-10 text-sm text-orange">
              {type === 0 && '点击后，在当前视角下，直接进行转场。'}
              {type === 1 && '点击后，视角移动到画面中央作为离场画面。'}
              {type === 2 && '点击后，视角移动到指定位置，作为离场画面。'}
            </div>
          </div>
          {type === 2 && (
            <SwitchViewSetting {...{ ath, atv, fov }} onLookToClick={lookTo} />
          )}
        </div>
        {type === 2 &&
          renderTip('拖动全景到指定位置并设置视角，以确定离场画面')}
      </>
    );
  };

  const renderEnterStartView = () => {
    const { type, ath, atv, fov } = state.enterInitView!;
    let items = enterInitItems;
    if (isEnterMatrix) {
      items = enterInitMItems;
    } else if (isExitMatrix) {
      items = enterInitMSItems;
    }
    return (
      <>
        <div className="mt-10 rounded bg-black-#28 py-15 px-10">
          <div>选择起始视角属性</div>
          <Dropdown
            className="mt-15"
            items={items}
            selected={type}
            onChange={handleEnterTypeChange}
          />
          <div className="mt-10 text-sm text-orange">
            {type === 0 && '以目标场景的初始视角作为进场后的首个画面。'}
            {type === 1 && (
              <>
                <p>使用原始场景中离场时的视角作为进场后的首个画面。</p>
                <p>多用于在两个相同的全景中切换白天和晚上等形式。</p>
              </>
            )}
            {type === 2 && '允许自由调整起始视角，以确定进场后的起始画面。'}
          </div>
        </div>
        {type === 2 && (
          <SwitchViewSetting {...{ ath, atv, fov }} onLookToClick={lookTo} />
        )}
      </>
    );
  };

  const renderEnterEndView = () => {
    const { type, ath, atv, fov } = state.enterTargetView!;
    return (
      <>
        <div className="mt-10 rounded bg-black-#28 py-15 px-10">
          <div>选择结束视角属性</div>
          <Dropdown
            className="mt-15"
            items={isEnterMatrix ? enterTargetMItems : enterTargetItems}
            selected={type}
            onChange={(typeId) =>
              setState((draft) => {
                draft.enterTargetView = { type: typeId };
              })
            }
          />
          <div className="mt-10 text-sm text-orange">
            {type === 0 && '不执行结束视角动作。'}
            {type === 1 && '允许自由调整结束视角，以便与起始视角形成引导动画。'}
          </div>
        </div>
        {type === 1 && (
          <SwitchViewSetting {...{ ath, atv, fov }} onLookToClick={lookTo} />
        )}
      </>
    );
  };

  const renderEnterSetting = () => {
    return (
      <>
        <div className="absolute top-70 left-10 w-200" style={{ zIndex: 2800 }}>
          <div className="rounded bg-black-#28 py-15 px-10">
            <div>设置起始与结束视角</div>
            <div className="mt-15 flex items-center justify-between">
              <Button
                scheme={enterTab === 'end' ? 'gray' : 'blue'}
                onClick={handleStartTabClick}
              >
                起始视角
              </Button>
              <img src={ImgArrow} alt="" className="h-20 w-20" />
              <Button
                scheme={enterTab === 'start' ? 'gray' : 'blue'}
                onClick={handleEndTabClick}
              >
                结束视角
              </Button>
            </div>
          </div>
          {enterTab === 'start' && renderEnterStartView()}
          {enterTab === 'end' && renderEnterEndView()}
        </div>
        {enterTab === 'start' &&
          state.enterInitView?.type === 2 &&
          renderTip('拖动全景到指定位置并设置视角，以确定进场后的首个画面。')}
        {enterTab === 'end' &&
          state.enterTargetView?.type === 1 &&
          renderTip(
            '拖动全景到指定位置并设置视角，以便与起始视角形成引导动画。'
          )}
      </>
    );
  };

  const renderCaptureButton = () => {
    return (
      <>
        <img
          src={ImgBlock}
          draggable={false}
          className="pointer-events-none absolute"
          style={{ width: 704, height: 404, zIndex: 2800 }}
          alt=""
        />
        <Button
          className="absolute mt-300"
          style={{ zIndex: 2800 }}
          onClick={handleCapture}
        >
          设置视角
        </Button>
      </>
    );
  };

  return (
    <StyledModal
      isOpen
      shouldCloseOnOverlayClick={false}
      onRequestClose={onRequestClose}
      header={<Title>场景切换动画高级设置</Title>}
      footer={<Button onClick={handleConfirm}>确认操作</Button>}
    >
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden text-white">
        <div className="absolute inset-0 bg-black-#28" id="switch" />
        {showCaputre && renderCaptureButton()}
        {renderHeader()}
        {tab === 'exit' && renderExitSetting()}
        {tab === 'enter' && renderEnterSetting()}
        {tab === 'preview' &&
          previewTip.isOn &&
          !!hotspot &&
          renderTip('点击热点图标，可以查看设置效果')}
      </div>
    </StyledModal>
  );
}

export default SwitchSettingModal;
